import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, patch, post } from "../../lib/request"
import { mergePropertyDetailRaw, unwrapPropertyDetailResponse } from "../../components/organization-settings/propertyDetailNormalize"

function propertyDetailIdFromRaw(raw) {
  const pd = unwrapPropertyDetailResponse(raw).pd
  if (pd?.id == null || pd.id === "") return null
  return pd.id
}

function mergeRawResponse(state, res) {
  if (!res || typeof res !== "object") return
  state.raw = mergePropertyDetailRaw(state.raw, res)
}

/** GET /api/v1/property-detail/clients/{clientId} — returns `{ pd, c }` (shape may vary). */
export const fetchPropertyDetailByClientId = createAsyncThunk(
  "propertyDetail/fetchByClientId",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await get(`property-details/clients/${clientId}`)
      return { clientId, response }
    } catch (error) {
      return rejectWithValue(error?.message || String(error))
    }
  },
)

/** POST /api/v1/property-detail/clients/{clientId} — create when no record exists yet. */
export const createPropertyDetail = createAsyncThunk(
  "propertyDetail/create",
  async ({ clientId, body }, { rejectWithValue }) => {
    try {
      const response = await post(`property-details/clients/${clientId}`, body)
      return response
    } catch (error) {
      return rejectWithValue(error?.message || String(error))
    }
  },
)

/** PATCH /api/v1/property-detail/{id} — update existing record. */
export const patchPropertyDetail = createAsyncThunk(
  "propertyDetail/patch",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      if (id == null || id === "") {
        return rejectWithValue("Missing property detail id")
      }
      const response = await patch(`property-details/${id}`, body)
      return response
    } catch (error) {
      return rejectWithValue(error?.message || String(error))
    }
  },
)

/**
 * Save property details: POST when no `pd.id`, PATCH when id exists (from GET or form).
 */
export const savePropertyDetail = createAsyncThunk(
  "propertyDetail/save",
  async ({ clientId, body, propertyDetailId }, { getState, rejectWithValue, dispatch }) => {
    const state = getState()
    const raw = state.propertyDetail?.raw
    const id = propertyDetailId ?? propertyDetailIdFromRaw(raw)

    try {
      if (id != null && id !== "") {
        return await dispatch(patchPropertyDetail({ id, body })).unwrap()
      }
      if (clientId == null || clientId === "") {
        return rejectWithValue("Missing client id")
      }
      return await dispatch(createPropertyDetail({ clientId, body })).unwrap()
    } catch (error) {
      return rejectWithValue(typeof error === "string" ? error : error?.message || "Failed to save property details")
    }
  },
)

/** POST /api/v1/property-detail/clients/{clientId}/refresh — re-scan website. */
export const refreshPropertyDetail = createAsyncThunk(
  "propertyDetail/refresh",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await post(`property-details/clients/${clientId}/refresh`, {})
      return { clientId, response }
    } catch (error) {
      return rejectWithValue(error?.message || String(error))
    }
  },
)

const initialState = {
  raw: null,
  loading: false,
  error: null,
  saving: false,
  saveError: null,
  refreshing: false,
  refreshError: null,
  lastFetchedClientId: null,
}

const propertyDetailSlice = createSlice({
  name: "propertyDetail",
  initialState,
  reducers: {
    clearPropertyDetail: (state) => {
      state.raw = null
      state.loading = false
      state.error = null
      state.saving = false
      state.saveError = null
      state.refreshing = false
      state.refreshError = null
      state.lastFetchedClientId = null
    },
    clearPropertyDetailSaveError: (state) => {
      state.saveError = null
    },
    clearPropertyDetailFetchError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPropertyDetailByClientId.pending, (state, action) => {
        state.loading = true
        state.error = null
        if (String(state.lastFetchedClientId ?? "") !== String(action.meta.arg ?? "")) {
          state.raw = null
        }
      })
      .addCase(fetchPropertyDetailByClientId.fulfilled, (state, action) => {
        state.loading = false
        state.lastFetchedClientId = action.payload.clientId ?? null
        const res = action.payload.response
        state.raw = res && typeof res === "object" ? unwrapPropertyDetailResponse(res) : { pd: null, c: null }
      })
      .addCase(fetchPropertyDetailByClientId.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? "Failed to load property details"
        state.raw = null
      })
      .addCase(createPropertyDetail.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(createPropertyDetail.fulfilled, (state, action) => {
        state.saving = false
        mergeRawResponse(state, action.payload)
      })
      .addCase(createPropertyDetail.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.payload ?? "Failed to create property details"
      })
      .addCase(patchPropertyDetail.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(patchPropertyDetail.fulfilled, (state, action) => {
        state.saving = false
        mergeRawResponse(state, action.payload)
      })
      .addCase(patchPropertyDetail.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.payload ?? "Failed to save property details"
      })
      .addCase(savePropertyDetail.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(savePropertyDetail.fulfilled, (state, action) => {
        state.saving = false
        mergeRawResponse(state, action.payload)
      })
      .addCase(savePropertyDetail.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.payload ?? "Failed to save property details"
      })
      .addCase(refreshPropertyDetail.pending, (state) => {
        state.refreshing = true
        state.refreshError = null
      })
      .addCase(refreshPropertyDetail.fulfilled, (state, action) => {
        state.refreshing = false
        mergeRawResponse(state, action.payload?.response)
      })
      .addCase(refreshPropertyDetail.rejected, (state, action) => {
        state.refreshing = false
        state.refreshError = action.payload ?? "Failed to re-scan website"
      })
  },
})

export const {
  clearPropertyDetail,
  clearPropertyDetailSaveError,
  clearPropertyDetailFetchError,
} = propertyDetailSlice.actions
export default propertyDetailSlice.reducer
